import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { actionEsOnMessage } from '@skynet/data-ws-es';
import type { RequestStatus, GenericError } from '@skynet/data-ws-core';
import { parseResponse } from '@skynet/data-ws-core';
import { DEFAULT_SORT_KEY, isSortKey } from 'src/store/utils';
import type { JsonTrader, JsonTradersResponse } from 'src/store/trading/traders/types';
import { TradingTradersCommands } from 'src/store/trading/traders/types';
import storage from 'src/store/storage';
import type { PersistedState } from 'redux-persist';
import { createMigrate, persistReducer } from 'redux-persist';
import { withResetState } from 'src/store/actions';

const allowedCommands = [TradingTradersCommands.Traders];

export interface TradingTradersState {
  list: JsonTrader[];
  requestStatus: RequestStatus;
  error: null | GenericError;

  targetTrader: string;
  sortKey: string;
  isSidebarOpen: boolean;
  searchInput: string;
  hideEmptyAccounts: boolean;
}

export const initialState: TradingTradersState = {
  list: [],
  requestStatus: null,
  error: null,

  targetTrader: '',
  sortKey: DEFAULT_SORT_KEY,
  isSidebarOpen: false,
  searchInput: '',
  hideEmptyAccounts: false,
};

export const tradersSlice = createSlice({
  name: 'traders',
  initialState: initialState,
  reducers: {
    setTargetTrader: (state, action: PayloadAction<{ tradingId: string }>) => {
      state.targetTrader = action.payload.tradingId;
    },

    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },

    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    setSortKey(state, action: PayloadAction<string>) {
      state.sortKey = action.payload;
    },

    setHideEmptyAccounts(state, action: PayloadAction<boolean>) {
      state.hideEmptyAccounts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionEsOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse<JsonTradersResponse>(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      if (error) {
        state.error = error;
        return;
      }

      const { command } = json;

      if (command === TradingTradersCommands.Traders) {
        const { list = [] } = json;
        state.list = list || [];
        state.requestStatus = 'success';
      }
    });
  },
});

export const tradesActions = { ...tradersSlice.actions };

/** Narrows a persisted payload to one carrying a usable `sortKey`. */
const hasSortKey = (state: PersistedState): state is PersistedState & { sortKey: string } =>
  typeof state === 'object' &&
  state !== null &&
  'sortKey' in state &&
  typeof state.sortKey === 'string';

/**
 * v0 of `trading/sidebar` persisted the old `'name'` default, which is not one
 * of `SORT_HANDLERS`' keys nor one of the sort Select's options: it sorted
 * nothing and, now that the Select is controlled, would render as a raw value.
 * Rewrite any key the app cannot apply to the default instead of dropping the
 * whole payload, so a deliberate choice like `name_desc` survives.
 */
export const tradersPersistMigrations = {
  1: (state: PersistedState) => {
    if (!hasSortKey(state) || isSortKey(state.sortKey)) return state;

    const migrated = { ...state, sortKey: DEFAULT_SORT_KEY };
    return migrated;
  },
};

const persistConfig = {
  key: 'trading/sidebar',
  version: 1,
  storage,
  whitelist: ['sortKey', 'hideEmptyAccounts'],
  migrate: createMigrate(tradersPersistMigrations),
};

const persistedReducer = persistReducer(
  persistConfig,
  withResetState(tradersSlice.reducer, initialState),
);

export default persistedReducer;
